"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api-client";
import type { Expense, ExpenseType, ExpenseCategory, PaymentMethod } from "@/lib/api-client";
import { useAppStore, currentView } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plus,
  Trash2,
  HandCoins,
  Receipt,
  FileX2,
  AlarmClock,
  Wallet,
  CheckCircle2,
  CalendarClock,
  Zap,
  Flame,
  Wifi,
  RefreshCw,
  Hammer,
  Landmark,
  Tag,
  TrendingUp,
  PieChart,
  Home,
  UserPlus,
  type LucideIcon,
  X,
} from "lucide-react";
import { isStaffRole, formatPrice, formatDate  } from "@/components/booking/format";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n/provider";

const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  "TAXES",
  "ELECTRICITY",
  "GAS",
  "INTERNET",
  "SUBSCRIPTION",
  "MAINTENANCE",
  "CUSTOM",
];
const PAYMENT_METHODS: PaymentMethod[] = ["CASH", "BANK_TRANSFER", "CARD", "OTHER"];

// One glyph per bill category — replaces badge soup in the list rows.
const CATEGORY_ICON: Record<ExpenseCategory, LucideIcon> = {
  TAXES: Landmark,
  ELECTRICITY: Zap,
  GAS: Flame,
  INTERNET: Wifi,
  SUBSCRIPTION: RefreshCw,
  MAINTENANCE: Hammer,
  CUSTOM: Tag,
};

type TabKey = "all" | "toPay" | "debts" | "dueSoon" | "paid" | "writtenOff";

export function ExpensesView() {
  const user = useAppStore((s) => s.user);
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [tab, setTab] = useState<TabKey>("toPay");
  const [editorOpen, setEditorOpen] = useState(false);
  const [houseFilter, setHouseFilter] = useState<string>("");
  // Inline (popup-free) action state: which row has which panel open.
  const [openRow, setOpenRow] = useState<
    { id: string; kind: "pay" | "writeoff" | "assign" } | null
  >(null);
  const [payForm, setPayForm] = useState({
    paidById: "",
    paidDate: new Date().toISOString().slice(0, 10),
    paymentMethod: "BANK_TRANSFER" as PaymentMethod,
  });
  const [writeOffNote, setWriteOffNote] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const now = new Date();
  const [scope, setScope] = useState<"year" | "month">("year");
  const [periodYear, setPeriodYear] = useState(now.getFullYear());
  const [periodMonth, setPeriodMonth] = useState(now.getMonth()); // 0-11

  const { data: productsData } = useQuery({
    queryKey: ["products", "manage"],
    queryFn: () => apiGet<{ products: any[] }>(`/api/products?manage=1`),
    enabled: !!user && (isStaffRole(user.role)),
  });
  const products = productsData?.products ?? [];

  const { data, isLoading } = useQuery({
    queryKey: ["expenses"],
    queryFn: () => apiGet<{ expenses: Expense[] }>("/api/admin/expenses"),
    enabled: !!user && (isStaffRole(user.role)),
  });

  const allExpenses = data?.expenses ?? [];
  // Optional house scope: empty = all houses combined.
  const all = houseFilter
    ? allExpenses.filter((e) => e.product?.id === houseFilter)
    : allExpenses;
  const toPay = all.filter((e) => e.computedStatus === "PENDING" || e.computedStatus === "OVERDUE");
  const overdue = all.filter((e) => e.computedStatus === "OVERDUE");
  const paid = all.filter((e) => e.computedStatus === "PAID");
  const writtenOff = all.filter((e) => e.computedStatus === "WRITTEN_OFF");
  const dueSoon = toPay.filter((e) => {
    const days = (new Date(e.dueDate).getTime() - Date.now()) / 86400000;
    return days <= 30;
  });

  const sum = (list: Expense[]) => list.reduce((s, e) => s + e.amount, 0);
  const totalToPay = sum(toPay);
  const totalOverdue = sum(overdue);
  const totalPaid = sum(paid);
  const totalDueSoon = sum(dueSoon);

  const list: Expense[] =
    tab === "all"
      ? all
      : tab === "toPay"
        ? toPay
        : tab === "debts"
          ? overdue
          : tab === "dueSoon"
            ? dueSoon
            : tab === "paid"
              ? paid
              : writtenOff;

  // Debts grouped by responsible member (unassigned bills group together).
  const debtGroups = (() => {
    const map = new Map<string, { name: string; total: number; count: number; items: Expense[] }>();
    for (const e of overdue) {
      const key = e.assignedTo?.id ?? "unassigned";
      const entry = map.get(key) ?? {
        name: e.assignedTo?.name ?? t("expenses.unassigned"),
        total: 0,
        count: 0,
        items: [],
      };
      entry.total += e.amount;
      entry.count += 1;
      entry.items.push(e);
      map.set(key, entry);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  })();

  // ---- Fixed vs punctual breakdown for the selected year/month ----
  const inPeriod = (e: Expense) => {
    const d = new Date(e.dueDate);
    if (d.getFullYear() !== periodYear) return false;
    if (scope === "month" && d.getMonth() !== periodMonth) return false;
    return true;
  };
  const periodList = all.filter(inPeriod);
  const periodFixed = periodList.filter((e) => e.type === "FIXED");
  const periodPunctual = periodList.filter((e) => e.type === "PUNCTUAL");
  const sumList = (list: Expense[]) => list.reduce((s, e) => s + e.amount, 0);
  const periodFixedTotal = sumList(periodFixed);
  const periodPunctualTotal = sumList(periodPunctual);
  const periodGrandTotal = periodFixedTotal + periodPunctualTotal;
  const monthMatrix = Array.from({ length: 12 }, (_, m) => {
    const items = all.filter(
      (e) =>
        new Date(e.dueDate).getFullYear() === periodYear &&
        new Date(e.dueDate).getMonth() === m
    );
    return {
      month: m,
      fixed: sumList(items.filter((e) => e.type === "FIXED")),
      punctual: sumList(items.filter((e) => e.type === "PUNCTUAL")),
      count: items.length,
    };
  });
  const years = Array.from(
    new Set(all.map((e) => new Date(e.dueDate).getFullYear()).concat(now.getFullYear()))
  ).sort((a, b) => b - a);
  const monthFormatter = new Intl.DateTimeFormat(
    locale === "fr" ? "fr-FR" : locale === "de" ? "de-DE" : "en-GB",
    { month: "short" }
  );
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  // "Where the money goes": top categories for the selected period.
  const categoryTotals = (() => {
    const map = new Map<ExpenseCategory, number>();
    for (const e of periodList) {
      map.set(e.category, (map.get(e.category) ?? 0) + e.amount);
    }
    return Array.from(map.entries())
      .map(([category, total]) => ({ category, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  })();
  const maxCategoryTotal = categoryTotals[0]?.total ?? 0;
  const maxMonthTotal = Math.max(1, ...monthMatrix.map((r) => r.fixed + r.punctual));
  const dueIn = (e: Expense) =>
    Math.ceil((new Date(e.dueDate).getTime() - Date.now()) / 86400000);

  // Family payers for the inline pay / assign / add panels.
  const { data: payersData } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => apiGet<{ familyPayers: any[] }>("/api/admin/dashboard"),
    enabled: !!openRow || editorOpen,
  });
  const payers = payersData?.familyPayers ?? [];

  const openPanel = (id: string, kind: "pay" | "writeoff" | "assign") => {
    setPayForm({
      paidById: "",
      paidDate: new Date().toISOString().slice(0, 10),
      paymentMethod: "BANK_TRANSFER",
    });
    setWriteOffNote("");
    setOpenRow(
      openRow && openRow.id === id && openRow.kind === kind
        ? null
        : { id, kind }
    );
  };

  const submitPay = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!openRow || !payForm.paidById) return;
    setActionBusy(true);
    try {
      await apiPost(
        "/api/admin/expenses/" + openRow.id + "/pay",
        payForm
      );
      toast.success(t("toast.expensePaid"));
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpenRow(null);
    } catch (err: any) {
      toast.error(err.message || t("toast.onlyFamilyCanPay"));
    } finally {
      setActionBusy(false);
    }
  };

  const submitWriteOff = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!openRow) return;
    setActionBusy(true);
    try {
      await apiPost(
        "/api/admin/expenses/" + openRow.id + "/writeoff",
        { note: writeOffNote || null }
      );
      toast.success(t("toast.writeOffDone"));
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpenRow(null);
    } catch (err: any) {
      toast.error(err.message || "Could not write off");
    } finally {
      setActionBusy(false);
    }
  };

  const assignTo = async (e: Expense, assignedToId: string | null) => {
    setActionBusy(true);
    try {
      await apiPatch("/api/admin/expenses/" + e.id, { assignedToId });
      toast.success(t("expenses.assignmentSaved"));
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpenRow(null);
    } catch (err: any) {
      toast.error(err.message || t("toast.actionFailed"));
    } finally {
      setActionBusy(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await apiDelete(`/api/admin/expenses/${id}`);
      toast.success(t("toast.expenseDeleted"));
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (err: any) {
      toast.error(err.message || "Could not delete");
    }
  };

  const statusBadge = (e: Expense) => {
    if (e.computedStatus === "OVERDUE")
      return (
        <Badge className="border-transparent bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300">
          {e.daysLate === 1
            ? t("expenses.dayLate", { count: 1 })
            : t("expenses.daysLate", { count: e.daysLate })}
        </Badge>
      );
    if (e.computedStatus === "PAID")
      return (
        <Badge className="border-transparent bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
          {t("expenses.badgePaid")}
          {e.paidLate ? ` · ${t("expenses.paidLateBadge")}` : ""}
        </Badge>
      );
    if (e.computedStatus === "WRITTEN_OFF")
      return (
        <Badge variant="secondary">{t("expenses.tabWrittenOff")}</Badge>
      );
    return (
      <Badge className="border-transparent bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
        {t("expenses.badgePending")}
      </Badge>
    );
  };

  const expenseCard = (e: Expense) => {
    const CatIcon = CATEGORY_ICON[e.category] ?? Tag;
    const days = dueIn(e);
    const urgency =
      e.computedStatus === "PENDING" && days === 0
        ? t("expenses.dueToday")
        : e.computedStatus === "PENDING" && days === 1
          ? t("expenses.dueTomorrow")
          : e.computedStatus === "PENDING" && days > 1 && days <= 30
            ? t("expenses.dueInDays", { count: days })
            : null;
    const dot =
      e.computedStatus === "OVERDUE"
        ? "bg-rose-500"
        : e.computedStatus === "PAID"
          ? "bg-emerald-500"
          : e.computedStatus === "WRITTEN_OFF"
            ? "bg-zinc-400"
            : "bg-amber-400";
    const amountTone =
      e.computedStatus === "OVERDUE"
        ? "text-rose-600 dark:text-rose-400"
        : e.computedStatus === "PAID"
          ? "text-emerald-600 dark:text-emerald-400"
          : e.computedStatus === "WRITTEN_OFF"
            ? "text-muted-foreground line-through decoration-muted-foreground/60"
            : "text-amber-600 dark:text-amber-400";
    return (
      <Card key={e.id} className="overflow-hidden py-0">
        <CardContent className="flex flex-col gap-3 p-3.5">
          <div className="flex items-center gap-3">
          <span className={`h-9 w-1 shrink-0 rounded-full ${dot}`} />
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <CatIcon className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="truncate text-sm font-semibold">
                {e.description}
              </span>
              {e.invoiceNumber && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {e.invoiceNumber}
                </span>
              )}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
              <span className="font-medium text-foreground/70">
                {e.product?.title ?? "—"}
              </span>
              <span>·</span>
              <span>
                {t(`expenses.cat.${e.category}`)}
                {e.category === "CUSTOM" && e.customLabel
                  ? ` (${e.customLabel})`
                  : ""}
              </span>
              <span>·</span>
              <span>
                {e.type === "FIXED"
                  ? t("expenses.typeFixed")
                  : t("expenses.typePunctual")}
              </span>
              {e.computedStatus === "PAID" && (
                <>
                  <span>·</span>
                  <span>
                    {t("expenses.paidBy")} {e.paidBy?.name ?? "—"}
                  </span>
                </>
              )}
              {e.computedStatus === "WRITTEN_OFF" && e.writeOffNote && (
                <>
                  <span>·</span>
                  <span>{e.writeOffNote}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              {(e.computedStatus === "PENDING" ||
                e.computedStatus === "OVERDUE") && (
                <button
                  type="button"
                  onClick={() => openPanel(e.id, "assign")}
                  title={t("expenses.changeAssignment")}
                  className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold transition-colors disabled:opacity-50"
                >
                  <UserPlus className="h-3 w-3" />
                  {e.assignedTo ? e.assignedTo.name : t("expenses.unassigned")}
                </button>
              )}
              <span
                className={`text-base font-bold tabular-nums ${amountTone}`}
              >
                {formatPrice(e.amount)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              {e.computedStatus === "OVERDUE" && (
                <span className="font-semibold text-rose-600 dark:text-rose-400">
                  {e.daysLate === 1
                    ? t("expenses.dayLate", { count: 1 })
                    : t("expenses.daysLate", { count: e.daysLate })}
                </span>
              )}
              {urgency && (
                <span className="text-amber-600 dark:text-amber-400">
                  {urgency}
                </span>
              )}
              <span>
                {e.computedStatus === "PAID"
                  ? formatDate(e.paidDate ?? e.dueDate, locale)
                  : formatDate(e.dueDate, locale)}
              </span>
            </div>
            <div className="flex gap-0.5">
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                title={t("expenses.invoice")}
                asChild
              >
                <a href={`/invoices/${e.id}`} target="_blank" rel="noreferrer">
                  <Receipt className="h-3.5 w-3.5" />
                </a>
              </Button>
              {(e.computedStatus === "PENDING" ||
                e.computedStatus === "OVERDUE") && (
                <>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
                    title={t("expenses.markPaid")}
                    onClick={() => openPanel(e.id, "pay")}
                  >
                    <HandCoins className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-muted-foreground hover:text-amber-600"
                    title={t("expenses.writeOff")}
                    onClick={() => openPanel(e.id, "writeoff")}
                  >
                    <FileX2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={() => remove(e.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          </div>
          {openRow && openRow.id === e.id && openRow.kind === "pay" && (
            <form
              onSubmit={submitPay}
              className="grid gap-2 rounded-lg border bg-muted/40 p-3 sm:grid-cols-[minmax(160px,220px)_auto_auto_auto] sm:items-end"
            >
              {payers.length === 0 ? (
                <p className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200 sm:col-span-4">
                  {t("expenses.noFamilyPayers")}
                </p>
              ) : (
                <>
                  <div className="grid gap-1">
                    <Label className="text-xs">{t("expenses.paidBy")}</Label>
                    <Select
                      value={payForm.paidById}
                      onValueChange={(v) =>
                        setPayForm((f) => ({ ...f, paidById: v }))
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder={t("expenses.selectPayer")} />
                      </SelectTrigger>
                      <SelectContent>
                        {payers.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} (@{p.username})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">{t("expenses.paidDate")}</Label>
                    <Input
                      type="date"
                      required
                      value={payForm.paidDate}
                      onChange={(e2) =>
                        setPayForm((f) => ({ ...f, paidDate: e2.target.value }))
                      }
                      className="h-9"
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">{t("expenses.paymentMethod")}</Label>
                    <Select
                      value={payForm.paymentMethod}
                      onValueChange={(v) =>
                        setPayForm((f) => ({
                          ...f,
                          paymentMethod: v as PaymentMethod,
                        }))
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map((m) => (
                          <SelectItem key={m} value={m}>
                            {t("expenses.method." + m)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setOpenRow(null)}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      size="sm"
                      disabled={actionBusy || !payForm.paidById}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {t("expenses.markPaid")}
                    </Button>
                  </div>
                </>
              )}
            </form>
          )}
          {openRow && openRow.id === e.id && openRow.kind === "writeoff" && (
            <form
              onSubmit={submitWriteOff}
              className="grid gap-2 rounded-lg border bg-muted/40 p-3"
            >
              <Textarea
                rows={2}
                placeholder={t("expenses.writeOffNote")}
                value={writeOffNote}
                onChange={(e2) => setWriteOffNote(e2.target.value)}
                className="bg-background"
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setOpenRow(null)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={actionBusy}
                  variant="destructive"
                >
                  {t("expenses.writeOffConfirm")}
                </Button>
              </div>
            </form>
          )}
          {openRow && openRow.id === e.id && openRow.kind === "assign" && (
            <div className="grid gap-2 rounded-lg border bg-muted/40 p-3">
              {payers.length === 0 ? (
                <p className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                  {t("expenses.noFamilyPayers")}
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {payers.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      disabled={actionBusy}
                      onClick={() => assignTo(e, p.id)}
                      className={
                        "rounded-full border px-3 py-1 text-xs font-semibold transition-colors hover:bg-muted disabled:opacity-60 " +
                        (e.assignedTo && e.assignedTo.id === p.id
                          ? "border-sky-300 bg-sky-100 text-sky-800 dark:border-sky-700 dark:bg-sky-900 dark:text-sky-200"
                          : "")
                      }
                    >
                      {p.name}
                    </button>
                  ))}
                  {e.assignedTo && (
                    <button
                      type="button"
                      disabled={actionBusy}
                      onClick={() => assignTo(e, null)}
                      className="rounded-full border border-dashed px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted disabled:opacity-60"
                    >
                      {t("expenses.unassign")}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Receipt className="h-6 w-6 text-amber-600" /> {t("expenses.title")}
          </h1>
          <p className="mt-1 text-muted-foreground">{t("expenses.subtitle")}</p>
        </div>
        <Button onClick={() => setEditorOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> {t("expenses.add")}
        </Button>
      </div>

      {/* House switcher: scopes every card, chart and list below */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Home className="h-4 w-4 text-muted-foreground" />
        <Select
          value={houseFilter || "all"}
          onValueChange={(v) => setHouseFilter(v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-[240px]">
            <SelectValue placeholder={t("expenses.allHouses")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("expenses.allHouses")}</SelectItem>
            {products.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {editorOpen && (
        <InlineExpenseForm
          products={products}
          payers={payers}
          defaultProductId={houseFilter}
          onDone={() => setEditorOpen(false)}
        />
      )}

      {/* Overview: amount-first summary band */}
      <div className="mb-6 grid gap-4 lg:grid-cols-[1fr_1.8fr]">
        <Card
          className={
            tab === "toPay"
              ? "cursor-pointer transition-all ring-2 ring-primary/50"
              : "cursor-pointer transition-all hover:ring-2 hover:ring-primary/40"
          }
          onClick={() => setTab("toPay")}
        >
          <CardContent className="flex h-full flex-col justify-between gap-5 p-5">
            <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <Wallet className="h-3.5 w-3.5" /> {t("expenses.cardToPay")}
            </div>
            <div>
              <div className="text-4xl font-bold tracking-tight text-amber-600">
                {formatPrice(totalToPay)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {t("expenses.bills", { count: toPay.length })}
                {overdue.length > 0 && (
                  <>
                    {" · "}
                    <span className="font-medium text-rose-600 dark:text-rose-400">
                      {formatPrice(totalOverdue)} {t("expenses.cardOverdue").toLowerCase()}
                    </span>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="grid h-full gap-3 p-4 sm:grid-cols-3">
            {([
              {
                key: "debts" as TabKey,
                icon: AlarmClock,
                label: t("expenses.cardOverdue"),
                amount: totalOverdue,
                count: overdue.length,
                tone: "text-rose-600 dark:text-rose-400",
                chip: "border-rose-200 bg-rose-50 hover:bg-rose-100/70 dark:border-rose-900 dark:bg-rose-950 dark:hover:bg-rose-900/40",
              },
              {
                key: "dueSoon" as TabKey,
                icon: CalendarClock,
                label: t("expenses.cardDueSoon"),
                amount: totalDueSoon,
                count: dueSoon.length,
                tone: "text-sky-600 dark:text-sky-400",
                chip: "border-sky-200 bg-sky-50 hover:bg-sky-100/70 dark:border-sky-900 dark:bg-sky-950 dark:hover:bg-sky-900/40",
              },
              {
                key: "paid" as TabKey,
                icon: CheckCircle2,
                label: t("expenses.totalPaid"),
                amount: totalPaid,
                count: paid.length,
                tone: "text-emerald-600 dark:text-emerald-400",
                chip: "border-emerald-200 bg-emerald-50 hover:bg-emerald-100/70 dark:border-emerald-900 dark:bg-emerald-950 dark:hover:bg-emerald-900/40",
              },
            ]).map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setTab(s.key)}
                className={
                  "rounded-lg border p-3 text-start transition-all hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 " +
                  s.chip +
                  (tab === s.key ? " ring-2 ring-primary/50" : "")
                }
              >
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <s.icon className="h-3.5 w-3.5" /> {s.label}
                </div>
                <div className={`mt-1 text-lg font-semibold tabular-nums ${s.tone}`}>
                  {formatPrice(s.amount)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("expenses.bills", { count: s.count })}
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Fixed vs punctual breakdown (yearly / monthly) */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-medium">{t("expenses.breakdownTitle")}</div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex overflow-hidden rounded-md border border-foreground">
                <Button
                  size="sm"
                  variant="ghost"
                  className={`h-8 rounded-none border-0 ${
                    scope === "year"
                      ? "bg-foreground text-background hover:bg-foreground/90 hover:text-background"
                      : "bg-background text-foreground hover:bg-muted"
                  }`}
                  onClick={() => setScope("year")}
                >
                  {t("expenses.scopeYear")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className={`h-8 rounded-none border-0 ${
                    scope === "month"
                      ? "bg-foreground text-background hover:bg-foreground/90 hover:text-background"
                      : "bg-background text-foreground hover:bg-muted"
                  }`}
                  onClick={() => setScope("month")}
                >
                  {t("expenses.scopeMonth")}
                </Button>
              </div>
              {scope === "month" && (
                <Select
                  value={String(periodMonth)}
                  onValueChange={(v) => setPeriodMonth(Number(v))}
                >
                  <SelectTrigger className="w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, m) => (
                      <SelectItem key={m} value={String(m)}>
                        {monthFormatter.format(new Date(2024, m, 1))}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={String(periodYear)} onValueChange={(v) => setPeriodYear(Number(v))}>
                <SelectTrigger className="w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 dark:border-sky-900 dark:bg-sky-950">
              <div className="text-xs text-muted-foreground">{t("expenses.fixedTotal")}</div>
              <div className="text-xl font-semibold text-sky-700 dark:text-sky-300">
                {formatPrice(periodFixedTotal)}
              </div>
              <div className="text-xs text-muted-foreground">
                {t("expenses.bills", { count: periodFixed.length })}
              </div>
            </div>
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 dark:border-violet-900 dark:bg-violet-950">
              <div className="text-xs text-muted-foreground">{t("expenses.punctualTotal")}</div>
              <div className="text-xl font-semibold text-violet-700 dark:text-violet-300">
                {formatPrice(periodPunctualTotal)}
              </div>
              <div className="text-xs text-muted-foreground">
                {t("expenses.bills", { count: periodPunctual.length })}
              </div>
            </div>
            <div className="rounded-lg border bg-muted/40 p-3">
              <div className="text-xs text-muted-foreground">{t("expenses.periodTotal")}</div>
              <div className="text-xl font-semibold">{formatPrice(periodGrandTotal)}</div>
              <div className="text-xs text-muted-foreground">
                {t("expenses.bills", { count: periodList.length })}
              </div>
            </div>
          </div>

          {/* Where the money goes: top categories for the period */}
          {categoryTotals.length > 0 && (
            <div className="mt-5">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <PieChart className="h-3.5 w-3.5" /> {t("expenses.topCategories")}
              </div>
              <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                {categoryTotals.map((c) => {
                  const Icon = CATEGORY_ICON[c.category] ?? Tag;
                  return (
                    <div key={c.category} className="flex items-center gap-2 text-sm">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="w-24 shrink-0 truncate text-muted-foreground">
                        {t(`expenses.cat.${c.category}`)}
                      </span>
                      <span className="h-1.5 min-w-4 flex-1 overflow-hidden rounded-full bg-muted">
                        <span
                          className="block h-full rounded-full bg-sky-400 dark:bg-sky-500"
                          style={{
                            width: `${
                              maxCategoryTotal
                                ? Math.max(6, Math.round((c.total / maxCategoryTotal) * 100))
                                : 0
                            }%`,
                          }}
                        />
                      </span>
                      <span className="w-20 shrink-0 text-end font-medium tabular-nums">
                        {formatPrice(c.total)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 12-month spending chart (year scope) */}
          {scope === "year" && (
            <div className="mt-5">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <TrendingUp className="h-3.5 w-3.5" /> {t("expenses.chartTitle")}
              </div>
              <div className="flex h-32 items-end gap-1.5">
                {monthMatrix.map((row) => {
                  const total = row.fixed + row.punctual;
                  const h = total ? Math.max(6, Math.round((total / maxMonthTotal) * 100)) : 0;
                  const fixedH = total ? Math.round((row.fixed / total) * h) : 0;
                  const isCurrent = periodYear === currentYear && row.month === currentMonth;
                  return (
                    <div
                      key={row.month}
                      className="flex min-w-0 flex-1 flex-col items-center gap-1"
                    >
                      <div
                        className={`flex w-full max-w-[38px] flex-col justify-end overflow-hidden rounded-sm ${
                          isCurrent ? "ring-2 ring-foreground/25" : ""
                        }`}
                        style={{ height: "104px" }}
                        title={`${monthFormatter.format(
                          new Date(2024, row.month, 1)
                        )}: ${total ? formatPrice(total) : t("expenses.noExpenses")}`}
                      >
                        {row.punctual > 0 && (
                          <div
                            className="w-full bg-violet-400 dark:bg-violet-500"
                            style={{ height: `${h - fixedH}%` }}
                          />
                        )}
                        {row.fixed > 0 && (
                          <div
                            className="w-full bg-sky-400 dark:bg-sky-500"
                            style={{ height: `${fixedH}%` }}
                          />
                        )}
                        {total === 0 && <div className="w-full bg-muted" style={{ height: "2px" }} />}
                      </div>
                      <span
                        className={`text-[10px] capitalize ${
                          isCurrent ? "font-bold text-foreground" : "text-muted-foreground"
                        }`}
                      >
                        {monthFormatter.format(new Date(2024, row.month, 1)).slice(0, 3)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-sky-400 dark:bg-sky-500" />{" "}
                  {t("expenses.typeFixed")}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-violet-400 dark:bg-violet-500" />{" "}
                  {t("expenses.typePunctual")}
                </span>
              </div>
            </div>
          )}

          {scope === "year" && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 pe-4 text-start text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("expenses.matrixMonth")}
                    </th>
                    <th className="px-3 py-2 text-end text-xs font-semibold uppercase tracking-wider text-sky-600">
                      {t("expenses.fixedTotal")}
                    </th>
                    <th className="px-3 py-2 text-end text-xs font-semibold uppercase tracking-wider text-violet-600">
                      {t("expenses.punctualTotal")}
                    </th>
                    <th className="px-3 py-2 text-end text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("expenses.periodTotal")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {monthMatrix.map((row) => {
                    const isCurrent =
                      periodYear === currentYear && row.month === currentMonth;
                    return (
                      <tr
                        key={row.month}
                        className={`border-b last:border-0 ${
                          isCurrent ? "bg-muted/60 font-medium" : ""
                        }`}
                      >
                        <td className="py-2 pe-4 capitalize">
                          {monthFormatter.format(new Date(2024, row.month, 1))}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {row.fixed ? formatPrice(row.fixed) : "—"}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {row.punctual ? formatPrice(row.punctual) : "—"}
                        </td>
                        <td className="px-3 py-2 text-end font-medium tabular-nums">
                          {row.fixed || row.punctual ? formatPrice(row.fixed + row.punctual) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2">
                    <td className="py-2 pe-4 font-semibold">{t("expenses.periodTotal")} {periodYear}</td>
                    <td className="px-3 py-2 text-end font-semibold tabular-nums">
                      {formatPrice(monthMatrix.reduce((s, r) => s + r.fixed, 0))}
                    </td>
                    <td className="px-3 py-2 text-end font-semibold tabular-nums">
                      {formatPrice(monthMatrix.reduce((s, r) => s + r.punctual, 0))}
                    </td>
                    <td className="px-3 py-2 text-end font-semibold tabular-nums">
                      {formatPrice(monthMatrix.reduce((s, r) => s + r.fixed + r.punctual, 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="toPay">
            {t("expenses.tabToPay", { count: toPay.length })}
          </TabsTrigger>
          <TabsTrigger value="debts">
            {t("expenses.tabDebts", { count: overdue.length })}
          </TabsTrigger>
          <TabsTrigger value="dueSoon">
            {t("expenses.tabDueSoon", { count: dueSoon.length })}
          </TabsTrigger>
          <TabsTrigger value="paid">
            {t("expenses.tabPaid", { count: paid.length })}
          </TabsTrigger>
          <TabsTrigger value="writtenOff">
            {t("expenses.tabWrittenOff", { count: writtenOff.length })}
          </TabsTrigger>
          <TabsTrigger value="all">{t("expenses.tabAll")}</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4 space-y-3">
          {isLoading ? (
            <Skeleton className="h-40 w-full rounded-xl" />
          ) : tab === "debts" && debtGroups.length > 0 ? (
            <>
              <div className="rounded-xl border bg-card p-4">
                <div className="mb-3 text-sm font-medium">{t("expenses.debtsSummary")}</div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {debtGroups.map((g) => (
                    <div
                      key={g.name}
                      className="flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3 dark:border-rose-900 dark:bg-rose-950"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{g.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {t("expenses.bills", { count: g.count })}
                        </div>
                      </div>
                      <div className="shrink-0 font-semibold text-rose-600">
                        {formatPrice(g.total)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {list.map(expenseCard)}
            </>
          ) : list.length === 0 ? (
            <div className="grid place-items-center rounded-xl border border-dashed py-12 text-center text-sm text-muted-foreground">
              {tab === "debts"
                ? t("expenses.noDebts")
                : tab === "paid"
                  ? t("expenses.noPaid")
                  : tab === "writtenOff"
                    ? t("expenses.noWrittenOff")
                    : t("expenses.noExpenses")}
            </div>
          ) : (
            list.map(expenseCard)
          )}
        </TabsContent>
      </Tabs>

    </div>
  );
}

// ---- Inline (popup-free) add-expense form ----
function InlineExpenseForm({
  products,
  payers,
  defaultProductId,
  onDone,
}: {
  products: any[];
  payers: any[];
  defaultProductId?: string;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    productId: defaultProductId ?? "",
    type: "FIXED" as ExpenseType,
    category: "ELECTRICITY" as ExpenseCategory,
    customLabel: "",
    description: "",
    amount: "",
    dueDate: "",
    periodStart: "",
    periodEnd: "",
    assignedToId: "",
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.productId) {
      toast.error(t("expenses.product"));
      return;
    }
    setBusy(true);
    try {
      await apiPost("/api/admin/expenses", {
        productId: form.productId,
        type: form.type,
        category: form.category,
        customLabel: form.category === "CUSTOM" ? form.customLabel : null,
        description: form.description,
        amount: Number(form.amount),
        dueDate: form.dueDate,
        periodStart: form.periodStart || null,
        periodEnd: form.periodEnd || null,
        assignedToId: form.assignedToId || null,
      });
      toast.success(t("toast.expenseAdded"));
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      onDone();
    } catch (err: any) {
      toast.error(err.message || "Could not add expense");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-semibold">{t("expenses.add")}</div>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={onDone}
            title={t("common.cancel")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <form onSubmit={submit} className="grid gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label>{t("expenses.product")}</Label>
              <Select
                value={form.productId}
                onValueChange={(v) => setForm((f) => ({ ...f, productId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("expenses.product")} />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>{t("expenses.assignedTo")}</Label>
              {payers.length === 0 ? (
                <p className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                  {t("expenses.noFamilyPayers")}
                </p>
              ) : (
                <Select
                  value={form.assignedToId || "none"}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, assignedToId: v === "none" ? "" : v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("expenses.unassigned")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("expenses.unassigned")}</SelectItem>
                    {payers.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} (@{p.username})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label>{t("expenses.type")}</Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm((f) => ({ ...f, type: v as ExpenseType }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FIXED">{t("expenses.typeFixed")}</SelectItem>
                  <SelectItem value="PUNCTUAL">{t("expenses.typePunctual")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>{t("expenses.category")}</Label>
              <Select
                value={form.category}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, category: v as ExpenseCategory }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {t("expenses.cat." + c)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.category === "CUSTOM" && (
              <div className="grid gap-1.5">
                <Label>{t("expenses.customLabel")}</Label>
                <Input
                  value={form.customLabel}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, customLabel: e.target.value }))
                  }
                  required
                />
              </div>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="grid gap-1.5 sm:col-span-2">
              <Label>{t("expenses.description")}</Label>
              <Input
                required
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
              />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("expenses.amount")}</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                required
                value={form.amount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, amount: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label>{t("expenses.dueDate")}</Label>
              <Input
                type="date"
                required
                value={form.dueDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, dueDate: e.target.value }))
                }
              />
            </div>
            {form.type === "FIXED" && (
              <>
                <div className="grid gap-1.5">
                  <Label>{t("expenses.periodStart")}</Label>
                  <Input
                    type="date"
                    value={form.periodStart}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, periodStart: e.target.value }))
                    }
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label>{t("expenses.periodEnd")}</Label>
                  <Input
                    type="date"
                    value={form.periodEnd}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, periodEnd: e.target.value }))
                    }
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={busy} className="gap-2">
              <Plus className="h-4 w-4" /> {t("expenses.add")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
